import { ILoginFormValues } from '@common/types/user';
import { SplitLayout } from '@components/layout';
import { AuthenticationHeader } from '@components/layout/authentication/AuthenticationHeader';
import { useAuth } from '@hooks/useAuth';
import { useLocaleFormRevalidation } from '@hooks/useLocaleFormRevalidation';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Button, CircularProgress, InputLabel, TextField, Typography } from '@mui/material';
import { createForgotPasswordSchema } from '@schemas/Authentication/ForgotPasswordSchema';
import { useForgotPasswordMutation } from '@services/accounts';
import { handleError } from '@utils/common';
import { pathToVP } from '@utils/route';
import { useFormik } from 'formik';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

const ForgotPassword = () => {
    const { formatMessage, locale } = useIntl();
    const ForgotPasswordSchema = useMemo(() => createForgotPasswordSchema(formatMessage), [formatMessage]);

    const navigate = usePrefixedNavigate();
    const { state: routeState } = useLocation();

    const { isLoggedIn, isUserLoading } = useAuth();
    const [searchParams] = useSearchParams();
    const next = searchParams.get('next');

    const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

    const formik = useFormik<Pick<ILoginFormValues, 'email'>>({
        initialValues: {
            email: routeState?.login_hint || '',
        },
        validationSchema: ForgotPasswordSchema,
        onSubmit: async (values, { setFieldError }) => {
            try {
                await forgotPassword({ email: values.email }).unwrap();
                navigate(
                    { pathname: `/auth/forgot-password-success`, search: searchParams.toString() },
                    {
                        state: { 'forgot-password-allow': true, email: values.email },
                    }
                );
            } catch (error: unknown) {
                handleError(
                    formatMessage({ id: 'auth.forgotPassword.error', defaultMessage: 'Forgot Password Error' }),
                    error
                );
                setFieldError(
                    'email',
                    formatMessage({
                        id: 'common.error.unexpected',
                        defaultMessage: 'Unexpected error occurred. Please try again later.',
                    })
                );
            }
        },
    });

    const {
        values: { email },
        handleChange,
        handleBlur,
        handleSubmit,
        touched: { email: emailTouched },
        errors: { email: emailErrors },
    } = formik;

    useLocaleFormRevalidation(formik, locale);

    if (isUserLoading) {
        return (
            <FormattedMessage
                id="common.loading"
                defaultMessage="Loading..."
                description="Loading message for usage while loading data in a page"
            />
        );
    }

    if (isLoggedIn) {
        window.location.replace(pathToVP(next ?? '/').toString());
    }

    return (
        <>
            <title>Victron Professional - Forgot Password</title>
            <SplitLayout>
                <AuthenticationHeader />
                <Alert severity="info" icon={<InfoOutlinedIcon color="info-main" />}>
                    <Typography fontFamily="Roboto" fontWeight={500} fontSize={16}>
                        <FormattedMessage
                            id="auth.forgotPassword.title"
                            defaultMessage="Password Request Process"
                            description="Title for password reset process. Used in: /auth/forgot-password"
                        />
                    </Typography>
                    <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={1}>
                        <FormattedMessage
                            id="auth.forgotPassword.description"
                            defaultMessage="Enter your email. We'll send you a link to securely create a new password for your account."
                            description="Instructions for password reset process. Used in: /auth/forgot-password"
                        />
                    </Typography>
                </Alert>
                <form onSubmit={handleSubmit}>
                    <InputLabel htmlFor="email" className="block text-sm font-medium text-neutral-950 mb-2 mt-4">
                        <FormattedMessage
                            id="common.fields.email.title"
                            defaultMessage="Email"
                            description="Label for email input field"
                        />
                    </InputLabel>
                    <TextField
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        variant="outlined"
                        placeholder={formatMessage({
                            id: 'common.fields.email.placeholder',
                            defaultMessage: 'Your email address',
                        })}
                        disabled={isLoading}
                        error={Boolean(emailTouched && emailErrors)}
                        helperText={(emailTouched && emailErrors) || ''}
                        size="small"
                        margin="none"
                        slotProps={{
                            input: { className: ' rounded-lg' },
                        }}
                    />
                    <Button
                        className="rounded-full black-border mt-6"
                        fullWidth
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                        disabled={isLoading}
                        type="submit"
                    >
                        <span>
                            <FormattedMessage
                                id="auth.forgotPassword.requestPassword"
                                defaultMessage={'Request Password'}
                                description="Text for request password button. Used in: /auth/forgot-password"
                            />
                        </span>
                    </Button>
                </form>
                <Button
                    disabled={isLoading}
                    to={{ pathname: '../login', search: searchParams.toString() }}
                    component={Link}
                    color="white"
                    className="rounded-full black-border mt-2"
                    fullWidth
                >
                    <FormattedMessage
                        id="common.buttons.back"
                        defaultMessage={'Go Back'}
                        description="Text for go back button"
                    />
                </Button>
            </SplitLayout>
        </>
    );
};

export { ForgotPassword };
